<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { getLanguageDisplayName } from 'dashboard/helper/languageHelper';
import { formatInAgentTimeZone } from 'shared/helpers/timeHelper';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const { t, locale } = useI18n();

const referer = computed(() => props.conversationAttributes.referer);
// Stored as a raw string: the visitor's Date.toString() for widget conversations and a UTC time for
// email ones. Show it in the agent's own time zone and language instead.
const initiatedAt = computed(() =>
  formatInAgentTimeZone(
    props.conversationAttributes.initiated_at?.timestamp,
    locale.value
  )
);

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageDisplayName(
    props.conversationAttributes.browser_language,
    locale.value
  )
);

const conversationLanguage = computed(() =>
  getLanguageDisplayName(
    props.conversationAttributes?.conversation_language,
    locale.value
  )
);

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const createdAtIp = computed(() => props.contactAttributes.created_at_ip);

const onCopyReferer = async () => {
  await copyTextToClipboard(referer.value);
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
};

const staticElements = computed(() =>
  [
    {
      content: initiatedAt,
      title: 'CONTACT_PANEL.INITIATED_AT',
      key: 'static-initiated-at',
      type: 'static_attribute',
    },
    {
      content: browserLanguage,
      title: 'CONTACT_PANEL.BROWSER_LANGUAGE',
      key: 'static-browser-language',
      type: 'static_attribute',
    },
    {
      content: conversationLanguage,
      title: 'CONTACT_PANEL.CONVERSATION_LANGUAGE',
      key: 'static-conversation-language',
      type: 'static_attribute',
    },
    {
      content: referer,
      title: 'CONTACT_PANEL.INITIATED_FROM',
      key: 'static-referer',
      type: 'static_attribute',
    },
    {
      content: browserName,
      title: 'CONTACT_PANEL.BROWSER',
      key: 'static-browser',
      type: 'static_attribute',
    },
    {
      content: platformName,
      title: 'CONTACT_PANEL.OS',
      key: 'static-platform',
      type: 'static_attribute',
    },
    {
      content: createdAtIp,
      title: 'CONTACT_PANEL.IP_ADDRESS',
      key: 'static-ip-address',
      type: 'static_attribute',
    },
  ].filter(attribute => !!attribute.content.value)
);
</script>

<template>
  <div class="conversation--details">
    <CustomAttributes
      :static-elements="staticElements"
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
    >
      <template #staticItem="{ element }">
        <ContactDetailsItem
          :key="element.title"
          :title="$t(element.title)"
          :value="element.content.value"
        >
          <template v-if="element.key === 'static-referer'" #button>
            <NextButton
              v-tooltip="$t('CONTACT_PANEL.COPY_REFERER')"
              ghost
              xs
              slate
              icon="i-lucide-clipboard"
              @click="onCopyReferer"
            />
          </template>
          <a
            v-if="element.key === 'static-referer'"
            v-tooltip="element.content.value"
            :href="element.content.value"
            rel="noopener noreferrer nofollow"
            target="_blank"
            class="text-n-brand line-clamp-2 break-all"
          >
            {{ element.content.value }}
          </a>
        </ContactDetailsItem>
      </template>
    </CustomAttributes>
  </div>
</template>
